/**
 * Functions for fetching Peacock asset details from the API
 */

import axios from "axios";

import { AssetResponse, FetchAssetDetailsOptions } from "./types";

import { PeacockNodeType } from "@/utils/shared-atom-types";
import { ATOM_SLUG_URL, ATOM_NODE_URL, ATOM_HEADERS } from "@/constants/api";

/**
 * Fetches asset details from the Peacock API using a slug, node ID, or provider path
 *
 * @remarks
 * This function automatically detects the identifier type:
 * - **Slug**: Starts with "/" (e.g., "/shows/saturday-night-live")
 *   - Uses ATOM_SLUG_URL endpoint
 * - **Node ID**: UUID v4 format with 8-4-4-4-12 hexadecimal characters (e.g., "f9339b60-0707-11ef-a279-a7afc301dae7")
 *   - Uses ATOM_NODE_URL endpoint
 * - **Provider Path**: Contains "/" but doesn't start with it (e.g., "provider_series_id/12345")
 *   - Uses ATOM_NODE_URL endpoint
 *
 * When multiple objects are returned from the API, only the object with type "ASSET/PROGRAMME"
 * or "CATALOGUE/SERIES" is returned.
 *
 * @param identifier - String containing a slug, node ID, or provider path
 * @param options - Optional query parameters for the request
 * @returns Promise that resolves to the full asset details response
 *
 * @example
 * ```typescript
 * // Fetch by slug
 * fetchAssetDetails("/shows/saturday-night-live")
 *   .then(asset => console.log(asset.attributes.title));
 *
 * // Fetch by node ID (UUID v4 format)
 * fetchAssetDetails("f9339b60-0707-11ef-a279-a7afc301dae7")
 *   .then(asset => console.log(asset.attributes.title));
 *
 * // Fetch by provider path
 * fetchAssetDetails("provider_series_id/12345")
 *   .then(asset => console.log(asset.attributes.title));
 *
 * // Fetch with nested data
 * fetchAssetDetails("provider_series_id/12345", { represent: "(items(items))" })
 *   .then(asset => console.log(asset.relationships.items.data));
 * ```
 *
 * @throws Will throw an error if the network request fails,
 *         if the API returns an empty array,
 *         or if a valid asset type is not found.
 */
const fetchAssetDetails = (
  identifier: string,
  options?: FetchAssetDetailsOptions,
): Promise<AssetResponse> => {
  const isSlug = identifier.startsWith("/");

  let url: string;
  if (isSlug) {
    url = `${ATOM_SLUG_URL}${encodeURIComponent(identifier)}`;
  } else {
    // All non-slug identifiers (node IDs, provider paths) use the node endpoint
    url = `${ATOM_NODE_URL}${identifier}`;
  }

  // Build query params
  const params: Record<string, string> = {};
  if (options?.represent) {
    params.represent = options.represent;
  }
  if (options?.contentSegments) {
    params.contentSegments = options.contentSegments;
  }
  if (options?.features) {
    params.features = options.features;
  }

  return axios
    .get(url, {
      headers: ATOM_HEADERS,
      params: Object.keys(params).length > 0 ? params : undefined,
    })
    .then((response) => {
      const data = response.data;

      // Throw if the API returns an empty array
      if (Array.isArray(data) && data.length === 0) {
        throw new Error("API returned an empty array");
      }

      if (Array.isArray(data)) {
        // If data is an array with more than one item, find the object with the specified type
        if (data.length > 1) {
          const filteredItem = data.find(
            (item) =>
              item.type === PeacockNodeType.Programme ||
              item.type === PeacockNodeType.Series,
          );

          if (filteredItem) {
            return filteredItem;
          }

          throw new Error("No valid asset type found in response");
        } else {
          // If data is an array with one item, return it directly
          return data[0];
        }
      }

      // If data is a single object, return it directly
      return data;
    });
};

export { fetchAssetDetails };
